import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Bell, Trash2, ArrowRight, Search, Heart, Loader2, MapPin, Plus, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import TopNavBar from '@/components/TopNavBar';
import ApisAIFooter from '@/components/ApisAIFooter';
import { listSavedSearches, getSavedSearchMatches, deleteSavedSearch, updateSavedSearch } from '@/utils/savedSearches';

function MatchTierBadge({ klass }) {
  if (!klass) return null;
  const colorMap = {
    perfect: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    good: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    weak: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold inline-flex items-center gap-1 ${colorMap[klass.tier] || colorMap.weak}`}>
      {klass.label}
    </span>
  );
}

function SavedSearchCard({ search, onDelete, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [matches, setMatches] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  const summary = [
    search.type && search.type !== 'all' ? search.type.replace('mioty', 'szczenięta') : null,
    search.rasa,
    search.miasto || search.wojewodztwo,
  ].filter(Boolean).join(' · ') || 'wszystkie oferty';

  const digestMode = search.digest_mode || 'instant';

  const toggleDigestMode = async () => {
    if (savingMode) return;
    const newMode = digestMode === 'instant' ? 'daily' : 'instant';
    setSavingMode(true);
    try {
      const res = await updateSavedSearch(search.id, { digest_mode: newMode });
      onUpdate?.(res.search);
    } catch { /* silent */ } finally {
      setSavingMode(false);
    }
  };

  const loadMatches = async () => {
    if (matches !== null) {
      setExpanded(!expanded);
      return;
    }
    setLoadingMatches(true);
    try {
      const data = await getSavedSearchMatches(search.id, 10);
      setMatches(data.matches || []);
      setExpanded(true);
    } catch {
      setMatches([]);
      setExpanded(true);
    } finally {
      setLoadingMatches(false);
    }
  };

  return (
    <article
      className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all"
      data-testid={`saved-search-${search.id}`}
    >
      <div className="p-5 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <h3 className="text-lg font-semibold text-white truncate">{search.name}</h3>
            {search.notify_email && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30 inline-flex items-center gap-1">
                <Bell className="w-2.5 h-2.5" />
                Powiadomienia
              </span>
            )}
            {search.notify_email && (
              <button
                type="button"
                onClick={toggleDigestMode}
                disabled={savingMode}
                title={digestMode === 'daily' ? 'Tryb: dzienny digest. Kliknij żeby zmienić na natychmiastowe.' : 'Tryb: natychmiastowo. Kliknij żeby zmienić na dzienny digest.'}
                className={`text-[10px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 transition cursor-pointer ${
                  digestMode === 'daily'
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25'
                    : 'bg-orange-500/15 text-orange-300 border-orange-500/40 hover:bg-orange-500/25'
                } ${savingMode ? 'opacity-50' : ''}`}
                data-testid={`saved-search-${search.id}-digest-toggle`}
              >
                {savingMode ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : digestMode === 'daily' ? '📬 Digest dzienny' : '⚡ Natychmiast'}
              </button>
            )}
          </div>
          <div className="text-sm text-gray-400 mb-2">{summary}</div>
          <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
            {search.match_count_at_creation > 0 && (
              <span>📊 {search.match_count_at_creation} dopasowań</span>
            )}
            {search.notify_email && (
              <span>✉️ {search.notify_email}</span>
            )}
            <span>Utworzone: {new Date(search.created_at).toLocaleDateString('pl-PL')}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button
            onClick={loadMatches}
            size="sm"
            variant="outline"
            className="rounded-xl border-white/15 bg-transparent text-white hover:bg-white/5 text-xs h-9"
            data-testid={`saved-search-${search.id}-toggle`}
          >
            {loadingMatches ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : expanded ? 'Ukryj' : 'Pokaż dopasowania'}
          </Button>
          <Button
            onClick={() => {
              if (window.confirm('Usunąć to wyszukiwanie?')) onDelete(search.id);
            }}
            size="sm"
            variant="outline"
            className="rounded-xl border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10 text-xs h-9"
            data-testid={`saved-search-${search.id}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {expanded && matches !== null && (
        <div className="border-t border-white/5 p-5 bg-black/20" data-testid={`saved-search-${search.id}-matches`}>
          {matches.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              <Search className="w-5 h-5 mx-auto mb-2" />
              Aktualnie brak dopasowań — powiadomimy Cię gdy się pojawią.
            </div>
          ) : (
            <div className="space-y-2.5">
              {matches.map((m) => (
                <Link
                  key={m.id}
                  to={m.url}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 hover:bg-white/[0.05] transition-all"
                  data-testid={`saved-search-match-${m.slug}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">{m.title}</div>
                    <div className="text-xs text-gray-500 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {m.city || m.subtitle || 'Polska'}
                      {m.breed && <span> · {m.breed}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <MatchTierBadge klass={m.match_class} />
                    <ArrowRight className="w-3.5 h-3.5 text-gray-500" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function MojeWyszukiwania() {
  const [searches, setSearches] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listSavedSearches().then((items) => {
      setSearches(items);
      setLoading(false);
    });
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteSavedSearch(id);
      setSearches((prev) => (prev || []).filter((s) => s.id !== id));
    } catch { /* silent */ }
  };

  const handleUpdate = (updated) => {
    setSearches((prev) => (prev || []).map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
  };

  return (
    <>
      <Helmet>
        <title>Moje wyszukiwania — ApisAI</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#0a0f1e] via-[#0e1628] to-[#0a0f1e] text-white">
        <TopNavBar />

        <section className="max-w-4xl mx-auto px-6 pt-16 pb-12">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs mb-4">
              <Heart className="w-3.5 h-3.5 text-rose-400" />
              Moje konto
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">
              Zapisane wyszukiwania
            </h1>
            <p className="text-gray-400 max-w-xl">
              Zarządzaj kryteriami wyszukiwania i włącz powiadomienia gdy pojawi się nowa pasująca oferta.
            </p>
          </div>

          {loading ? (
            <div className="py-20 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
              <p className="text-sm text-gray-500">Ładuję wyszukiwania…</p>
            </div>
          ) : (searches || []).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center" data-testid="saved-searches-empty">
              <Bell className="w-10 h-10 text-gray-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Nie masz jeszcze zapisanych wyszukiwań</h2>
              <p className="text-gray-400 max-w-md mx-auto mb-6">
                Wejdź do katalogu, ustaw filtry (rasa, miasto, województwo) i kliknij <strong>"Zapisz wyszukiwanie"</strong>. Powiadomimy Cię o nowych ofertach.
              </p>
              <Link to="/katalog">
                <Button className="rounded-full bg-white text-black hover:bg-white/90 font-semibold px-6 h-11">
                  <Plus className="w-4 h-4 mr-1.5" />
                  Przejdź do katalogu
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="saved-searches-list">
              {searches.map((s) => (
                <SavedSearchCard key={s.id} search={s} onDelete={handleDelete} onUpdate={handleUpdate} />
              ))}
            </div>
          )}

          {(searches || []).length > 0 && (
            <div className="mt-8 rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 text-sm text-amber-200/80 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <strong>Anonimowy zapis:</strong> Twoje wyszukiwania są zapisane lokalnie (per przeglądarka).
                Aby mieć je dostępne na innych urządzeniach — załóż konto.
              </div>
            </div>
          )}
        </section>

        <ApisAIFooter pageType="account" />
      </div>
    </>
  );
}
